import React, { useRef } from 'react'
import { Tooltip } from 'antd'
import { EditOutlined, LoadingOutlined, CheckOutlined, LockOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'

import { Button } from '../../design-system/Button'
import { Stack } from '../../design-system/Stack'
import { Text } from '../../design-system/Text'
import IntlMessages from '../../util/IntlMessages'
import { useInlineEditGroup } from './context'
import { useSaveStatus } from './use-save-status'

export interface IEditorActions<TValue> {
  /** Report the value Save will commit. Editors call this on every change. */
  setDraft: (value: TValue) => void
  /** Whether the draft differs from what is stored - Save stays disabled until it does. */
  setDirty: (dirty: boolean) => void
  /** Whether the draft could be saved. Only fields with a rule need to call this. */
  setValid: (valid: boolean) => void
  cancel: () => void
}

export interface IEditableRowProps<TValue> {
  /** Names the field, and identifies it to the edit group. One id, not a separate key. */
  labelId: string
  label: React.ReactNode
  display: React.ReactNode
  /** Secondary text set beside the value, e.g. the region a country still needs. */
  note?: React.ReactNode
  /** i18n id. Renders a padlock with a tooltip and makes the row inert. */
  lockReason?: string
  renderEditor: (actions: IEditorActions<TValue>) => React.ReactNode
  onCommit: (value: TValue) => Promise<void>
  /** Called with this row's label id when another row is holding unsaved changes. */
  onBlocked?: (labelId: string) => void
  /**
   * Shown inside this row while it is open, when an attempt to edit another one was turned away.
   * Rendered on the edit that is actually blocking, rather than above the whole page.
   */
  blockedMessage?: React.ReactNode
}

export function EditableRow<TValue>(props: IEditableRowProps<TValue>): React.ReactElement {
  const { labelId, label, display, note, lockReason, renderEditor, onCommit, onBlocked, blockedMessage } = props
  const group = useInlineEditGroup()
  const { status, errorMessage, runSave, clearError } = useSaveStatus()
  const { formatMessage } = useIntl()

  // The editor owns its own draft; the row only needs the latest value to hand to Save. A flag
  // rather than an undefined sentinel, so TValue may itself be undefined.
  const draft = useRef<{ value: TValue } | null>(null)

  const fieldName = formatMessage({ id: labelId })
  const isEditing = group.openKey === labelId

  const cancel = () => {
    draft.current = null
    clearError()
    group.close()
  }

  const save = async () => {
    if (!draft.current) {
      cancel()
      return
    }
    // The row stays open until the save lands, so a failure keeps the typed value on screen
    // instead of discarding it behind a warning icon.
    if (await runSave(() => onCommit(draft.current!.value))) {
      draft.current = null
      group.close()
    }
  }

  const activate = () => {
    if (status === 'saving') return
    clearError()
    if (!group.requestOpen(labelId)) {
      onBlocked?.(labelId)
    }
  }

  const affordance = (): React.ReactNode => {
    if (isEditing) {
      return (
        <Stack direction="horizontal" align="center" gap="xs" wrap>
          <Button variant="primary" loading={status === 'saving'} disabled={!group.isDirty || !group.isValid} onClick={save}>
            <IntlMessages id="app.save" />
          </Button>
          {/* The write is already in flight; closing the row here would let it land with nothing on
              screen to report a failure. */}
          <Button disabled={status === 'saving'} onClick={cancel}>
            <IntlMessages id="app.cancel" />
          </Button>
        </Stack>
      )
    }
    return (
      <Stack direction="horizontal" align="center" gap="xs">
        {status === 'saving' && (
          <span className="vt-inline-row-status">
            <LoadingOutlined />
          </span>
        )}
        {status === 'saved' && (
          <span className="vt-inline-row-status vt-inline-row-status--saved">
            <CheckOutlined /> <IntlMessages id="locationGeneral.saved" />
          </span>
        )}
      </Stack>
    )
  }

  // Sits with the value rather than in the row's action slot: it marks that one field as editable,
  // so the design keeps it next to what it edits instead of stranded at the far right.
  const valueAffordance = (): React.ReactNode => {
    if (lockReason) {
      return (
        <Tooltip title={formatMessage({ id: lockReason })}>
          <span className="vt-inline-row-locked">
            <LockOutlined />
          </span>
        </Tooltip>
      )
    }
    if (status === 'saving') {
      return null
    }
    return (
      <button type="button" className="vt-inline-row-edit" aria-label={formatMessage({ id: 'locationGeneral.editField' }, { field: fieldName })} onClick={activate}>
        <EditOutlined />
      </button>
    )
  }

  // The pencil is the keyboard-reachable control; clicking anywhere on a closed row is a mouse
  // shortcut to the same thing. Left as a plain div rather than given a role: a button wrapping the
  // pencil button would nest two controls, and activate() is idempotent when the click bubbles.
  const isClickable = !isEditing && !lockReason && status !== 'saving'

  return (
    <div className={`vt-inline-row${isEditing ? ' vt-inline-row--editing' : ''}${isClickable ? ' vt-inline-row--clickable' : ''}`} onClick={isClickable ? activate : undefined}>
      <div className="vt-inline-row-label">
        <Text tone="secondary">{label}</Text>
      </div>
      <div className="vt-inline-row-main">
        {isEditing ? (
          // The message sits under the editor, not beside Save: the action column is sized to its
          // content, so a sentence in there stretches it and squeezes the rest of the row.
          <Stack gap="xs">
            {renderEditor({
              setDraft: (value) => {
                draft.current = { value }
              },
              setDirty: group.setDirty,
              setValid: group.setValid,
              cancel,
            })}
            {blockedMessage && (
              <span role="status">
                <Text tone="warning" size="sm">
                  {blockedMessage}
                </Text>
              </span>
            )}
            {status === 'error' && (
              <Text tone="danger" size="sm">
                {errorMessage}
              </Text>
            )}
          </Stack>
        ) : (
          <div className="vt-inline-row-valueline">
            <span className="vt-inline-row-value">
              {display ?? (
                <Text tone="tertiary">
                  <IntlMessages id="app.notSet" />
                </Text>
              )}
              {note && (
                <Text tone="tertiary" size="sm">
                  {note}
                </Text>
              )}
            </span>
            {valueAffordance()}
          </div>
        )}
      </div>
      <div className="vt-inline-row-action">{affordance()}</div>
      {/* The tick alone is not feedback for anyone who cannot see it. */}
      <span className="vt-visually-hidden" aria-live="polite">
        {status === 'saved' ? formatMessage({ id: 'locationGeneral.savedField' }, { field: fieldName }) : ''}
      </span>
    </div>
  )
}
